import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { cartActions } from "../../../store/cart-slice";
import { ProductContext } from "../../../context/ProductContext";
import { getProductTitle } from "../../../utils/productDisplayName";

const Container = styled.figure`
  position: relative;
  box-sizing: border-box;
  background-color: var(--bg-100);
  width: 96%;
  padding: var(--spacing-sm);
  margin: 0 0 10px 0;
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  box-shadow: var(--shadow-small);
  border-radius: var(--border-radius-base);
  border: 1px solid var(--bg-300);
  opacity: ${(props) => (props.$outOfStock ? 0.75 : 1)};
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;

  &:hover {
    box-shadow: var(--shadow-medium);
  }
`;

const Picture = styled.picture`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 84px;
  height: 84px;
  border-radius: var(--border-radius-base);
  background-color: var(--bg-200);
  overflow: hidden;
  position: relative;
`;

const Image = styled.img`
  width: 82%;
  height: 82%;
  object-fit: contain;
  display: block;
  margin: 0 auto;
`;

const StockBadge = styled.span`
  position: absolute;
  bottom: 2px;
  left: 2px;
  right: 2px;
  text-align: center;
  background-color: var(--bg-100);
  color: var(--error-color);
  font-size: 0.55rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  border-radius: var(--border-radius-small);
  padding: 2px 0;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  width: 100%;
  min-width: 0;
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--spacing-xs);
`;

const NameBlock = styled.div`
  min-width: 0;
`;

const ProductName = styled.h4`
  font-size: var(--font-size-small);
  font-weight: 500;
  color: var(--text-100);
  line-height: 1.25;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const NicotineBadge = styled.span`
  display: inline-block;
  margin-top: 4px;
  padding: 1px 8px;
  border-radius: var(--border-radius-large);
  background-color: var(--primary-300);
  color: var(--primary-100);
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

const RemoveButton = styled.button`
  all: unset;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  color: var(--text-200);
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: var(--bg-200);
    color: var(--error-color);
  }
`;

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-xs);
  flex-wrap: wrap;
`;

const AddContainer = styled.div`
  background-color: var(--bg-200);
  border: 1px solid var(--bg-300);
  border-radius: var(--border-radius-large);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 3px;
`;

const StepButton = styled.button`
  background-color: var(--bg-100);
  border: none;
  padding: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  color: var(--text-100);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--primary-300);
  }
`;

const Input = styled.input`
  width: 28px;
  height: 22px;
  padding: 0;
  margin: 0 2px;
  background-color: transparent;
  border: none;
  text-align: center;
  font-size: var(--font-size-small);
  font-weight: 600;
  color: var(--text-100);

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.15;
`;

const LineTotal = styled.span`
  font-size: var(--font-size-base);
  font-weight: 700;
  color: var(--text-100);
`;

const UnitPrice = styled.span`
  font-size: 0.7rem;
  color: var(--text-200);
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StrikePrice = styled.span`
  text-decoration: line-through;
  opacity: 0.7;
`;

const CartProduct = ({ item }) => {
  const { t } = useTranslation();
  const { currencyTag } = useContext(ProductContext);
  const [inputValue, setInputValue] = useState(item.quantity);
  const dispatch = useDispatch();
  const outOfStock = item.product.is_in_stock !== "in_stock";

  const deleteCartItem = (id) => {
    dispatch(cartActions.removeFromCart(id));
  };

  const handleAddOne = () => {
    const newQuantity = inputValue + 1;

    dispatch(
      cartActions.updateCart({
        product: item.product,
        quantity: newQuantity,
      })
    );
  };
  const handleRemoveOne = () => {
    const newQuantity = inputValue - 1;
    if (newQuantity === 0) {
      deleteCartItem(item.product.id);
    }
    dispatch(
      cartActions.updateCart({
        product: item.product,
        quantity: newQuantity,
      })
    );
  };
  useEffect(() => {
    setInputValue(item.quantity);
  }, [item.quantity]);

  const handleChange = (event) => {
    const value = Math.max(1, Math.min(999, Number(event.target.value)));
    setInputValue(value);
    dispatch(
      cartActions.updateCart({
        product: item.product,
        quantity: value,
      })
    );
  };
  // Try to find a non-primary image; if none exist, use the primary image as fallback
  const primaryImage =
    item.product.images.find((img) => !img.is_primary) ||
    item.product.images.find((img) => img.is_primary);

  const hasDiscount = item.product.price > item.product.discount_price;
  const lineTotal = item.product.discount_price * item.quantity;

  return (
    <Container $outOfStock={outOfStock}>
      <Picture>
        {primaryImage && (
          <Image
            src={primaryImage.thumbnail}
            alt={getProductTitle(item.product.category_name, item.product.name)}
            srcSet={`${primaryImage.thumbnail} 320w, ${primaryImage.large} 480w, ${primaryImage.large} 800w`}
            sizes="(max-width: 320px) 300px, (max-width: 480px) 440px, 800px"
            loading="lazy"
            className="primary-image"
          />
        )}
        {outOfStock && (
          <StockBadge>{t("BUTTONS.OUT_OF_STOCK")}</StockBadge>
        )}
      </Picture>
      <Right>
        <TopRow>
          <NameBlock>
            <ProductName>
              {getProductTitle(item.product.category_name, item.product.name)}
            </ProductName>
            {item.product.nicotine != null && (
              <NicotineBadge>{item.product.nicotine}MG</NicotineBadge>
            )}
          </NameBlock>
          <RemoveButton
            onClick={() => deleteCartItem(item.product.id)}
            aria-label={t("ARIA.REMOVE_ITEM")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                deleteCartItem(item.product.id);
              }
            }}
          >
            <svg width="16px" height="16px" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 6.38597C3 5.90152 3.34538 5.50879 3.77143 5.50879L6.43567 5.50832C6.96502 5.49306 7.43202 5.11033 7.61214 4.54412C7.61688 4.52923 7.62232 4.51087 7.64185 4.44424L7.75665 4.05256C7.8269 3.81241 7.8881 3.60318 7.97375 3.41617C8.31209 2.67736 8.93808 2.16432 9.66147 2.03297C9.84457 1.99972 10.0385 1.99986 10.2611 2.00002H13.7391C13.9617 1.99986 14.1556 1.99972 14.3387 2.03297C15.0621 2.16432 15.6881 2.67736 16.0264 3.41617C16.1121 3.60318 16.1733 3.81241 16.2435 4.05256L16.3583 4.44424C16.3778 4.51087 16.3833 4.52923 16.388 4.54412C16.5682 5.11033 17.1278 5.49353 17.6571 5.50879H20.2286C20.6546 5.50879 21 5.90152 21 6.38597C21 6.87043 20.6546 7.26316 20.2286 7.26316H3.77143C3.34538 7.26316 3 6.87043 3 6.38597Z"
                fill="currentColor"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.5956 22.0001H12.4044C15.1871 22.0001 16.5785 22.0001 17.4831 21.1142C18.3878 20.2283 18.4803 18.7751 18.6654 15.8686L18.9321 11.6807C19.0326 10.1037 19.0828 9.31524 18.6289 8.81558C18.1751 8.31592 17.4087 8.31592 15.876 8.31592H8.12404C6.59127 8.31592 5.82488 8.31592 5.37105 8.81558C4.91722 9.31524 4.96744 10.1037 5.06788 11.6807L5.33459 15.8686C5.5197 18.7751 5.61225 20.2283 6.51689 21.1142C7.42153 22.0001 8.81289 22.0001 11.5956 22.0001ZM10.2463 12.1886C10.2051 11.7548 9.83753 11.4382 9.42537 11.4816C9.01321 11.525 8.71251 11.9119 8.75372 12.3457L9.25372 17.6089C9.29494 18.0427 9.66247 18.3593 10.0746 18.3159C10.4868 18.2725 10.7875 17.8856 10.7463 17.4518L10.2463 12.1886ZM14.5746 11.4816C14.9868 11.525 15.2875 11.9119 15.2463 12.3457L14.7463 17.6089C14.7051 18.0427 14.3375 18.3593 13.9254 18.3159C13.5132 18.2725 13.2125 17.8856 13.2537 17.4518L13.7537 12.1886C13.7949 11.7548 14.1625 11.4382 14.5746 11.4816Z"
                fill="currentColor"
              />
            </svg>
          </RemoveButton>
        </TopRow>
        <BottomRow>
          <AddContainer>
            <StepButton
              onClick={() => handleRemoveOne()}
              aria-label={t("ARIA.DECREASE_QUANTITY")}
            >
              <svg
                viewBox="0 0 160 161"
                fill="none"
                className="svg-icon"
                style={{ width: "16px", height: "16px" }}
              >
                <path
                  d="M108.69 74.379H49.407c-3.359 0-6.083 2.74-6.083 6.12 0 3.382 2.724 6.122 6.084 6.122h59.282c3.36 0 6.084-2.74 6.084-6.121 0-3.38-2.724-6.121-6.084-6.121z"
                  fill="var(--text-200)"
                ></path>
              </svg>
            </StepButton>
            <Input
              type="number"
              max="999"
              min="0"
              aria-label={t("PRODUCT.QUANTITY")}
              value={inputValue}
              onChange={handleChange}
            />
            <StepButton
              onClick={() => handleAddOne()}
              aria-label={t("ARIA.INCREASE_QUANTITY")}
            >
              <svg
                viewBox="0 0 161 161"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="svg-icon"
                style={{ width: "16px", height: "16px" }}
              >
                <path
                  d="M119.668 74.283H85.76V40.375a6.217 6.217 0 00-12.434 0v33.908H39.418a6.217 6.217 0 000 12.434h33.908v33.908a6.217 6.217 0 1012.434 0V86.717h33.908a6.217 6.217 0 100-12.434z"
                  fill="var(--text-200)"
                ></path>
              </svg>
            </StepButton>
          </AddContainer>

          <PriceBlock>
            <LineTotal>
              {currencyTag}
              {lineTotal.toFixed(2)}
            </LineTotal>
            <UnitPrice>
              {hasDiscount && (
                <StrikePrice>
                  {currencyTag}
                  {item.product.price.toFixed(2)}
                </StrikePrice>
              )}
              <span>
                {currencyTag}
                {item.product.discount_price.toFixed(2)}/pc
              </span>
            </UnitPrice>
          </PriceBlock>
        </BottomRow>
      </Right>
    </Container>
  );
};

export default CartProduct;
